import { FC, useMemo, useState } from "react";
import { Table, Tag, Empty, Spin, Modal } from "antd";
import { Calendar, Users, MapPin, Package, Clock } from "lucide-react";
import { useMissionControlStore } from "../../mission-control.store";
import { useGetBookings } from "~/services/booking";
import { capitalize, formatDateTime } from "~/utils";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { ContainerInfoCard } from "~/components/common/container-info";

type Props = {};

export const ScheduleListView: FC<Props> = () => {
  const { selectedProjectId } = useMissionControlStore();
  const [selectedService, setSelectedService] = useState<any | null>(null);

  const { data: bookingsData, isLoading } = useGetBookings(
    selectedProjectId
      ? {
          projectId: selectedProjectId,
          pageSize: 500,
        }
      : undefined
  );

  const allServiceTickets = useMemo(() => {
    if (!bookingsData?.items) return [];

    const tickets = bookingsData.items.flatMap((booking) =>
      (booking.serviceTicket || []).map((ticket: any) => ({
        ...ticket,
        booking: {
          id: booking.id,
          referenceNumber: booking.referenceNumber,
          client: booking.client,
        },
      }))
    );

    // Sort by service date/time
    return tickets.sort((a, b) => {
      const dateA = dayjs(a.serviceDatetime || a.startDatetime);
      const dateB = dayjs(b.serviceDatetime || b.startDatetime);
      return dateA.diff(dateB);
    });
  }, [bookingsData]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      completed: "success",
      "in-progress": "processing",
      scheduled: "blue",
      draft: "default",
      cancelled: "error",
    };
    return statusMap[status?.toLowerCase()] || "default";
  };

  const columns = [
    {
      title: "Date/Time",
      dataIndex: "serviceDatetime",
      key: "datetime",
      width: 180,
      render: (datetime: string, record: any) => {
        // Combine date from serviceDatetime with time from startDatetime
        let displayDate;
        if (record.serviceDatetime && record.startDatetime) {
          const serviceDate = dayjs(record.serviceDatetime);
          const startTimeData = dayjs(record.startDatetime);
          displayDate = serviceDate
            .hour(startTimeData.hour())
            .minute(startTimeData.minute())
            .second(startTimeData.second());
        } else {
          displayDate = dayjs(datetime || record.startDatetime);
        }

        return (
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-gray-400" />
            <div>
              <div className="font-medium">{displayDate.format("MMM D, YYYY")}</div>
              <div className="text-xs text-gray-500">
                {displayDate.format("h:mm A")}
              </div>
            </div>
          </div>
        );
      },
      sorter: (a: any, b: any) => {
        const dateA = dayjs(a.serviceDatetime || a.startDatetime);
        const dateB = dayjs(b.serviceDatetime || b.startDatetime);
        return dateA.diff(dateB);
      },
    },
    {
      title: "Service Ticket",
      dataIndex: "serial",
      key: "serial",
      width: 150,
      render: (serial: string, record: any) => (
        <span
          className="font-medium text-blue-600 cursor-pointer hover:text-blue-800"
          onClick={() => setSelectedService(record)}
        >
          {serial}
        </span>
      ),
    },
    {
      title: "Booking",
      dataIndex: "booking",
      key: "booking",
      width: 150,
      render: (booking: any) => (
        <Link
          to={`/bookings/${booking.id}`}
          className="text-blue-600 hover:text-blue-800"
        >
          {booking.referenceNumber}
        </Link>
      ),
    },
    {
      title: "Client",
      key: "client",
      width: 200,
      render: (_: any, record: any) => {
        const client = record.booking?.client;
        return (
          <span>
            {client?.companyName ||
              `${client?.firstName || ""} ${client?.lastName || ""}`.trim() ||
              "Unknown"}
          </span>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 120,
      render: (type: string) => <Tag color="blue">{capitalize(type)}</Tag>,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 180,
      render: (location: any) => {
        if (!location) return <span className="text-gray-400">-</span>;

        const locationParts = [location.city, location.state, location.zip].filter(
          Boolean
        );

        return locationParts.length > 0 ? (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-gray-400" />
            <span className="text-sm truncate">{locationParts.join(", ")}</span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        );
      },
    },
    {
      title: "Staff",
      dataIndex: "staffs",
      key: "staffs",
      width: 100,
      render: (staffs: any[]) =>
        staffs && staffs.length > 0 ? (
          <div className="flex items-center gap-2">
            <Users size={14} className="text-gray-400" />
            <span className="text-sm">{staffs.length}</span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Inventory",
      dataIndex: "inventories",
      key: "inventories",
      width: 100,
      render: (inventories: any[]) =>
        inventories && inventories.length > 0 ? (
          <div className="flex items-center gap-2">
            <Package size={14} className="text-gray-400" />
            <span className="text-sm">{inventories.length}</span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{capitalize(status || "pending")}</Tag>
      ),
      filters: [
        { text: "Scheduled", value: "scheduled" },
        { text: "In Progress", value: "in-progress" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      onFilter: (value: any, record: any) => record.status === value,
    },
    {
      title: "Duration",
      key: "duration",
      width: 100,
      render: (_: any, record: any) => {
        if (!record.timeIn || !record.timeOut) {
          return <span className="text-gray-400">-</span>;
        }
        const duration = dayjs(record.timeOut).diff(dayjs(record.timeIn), "hour", true);
        return (
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-gray-400" />
            <span className="text-sm">{duration.toFixed(1)}h</span>
          </div>
        );
      },
    },
  ];

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="text-blue-600" size={24} />
          <div>
            <h2 className="text-xl font-semibold">Schedule View</h2>
            <p className="text-sm text-gray-500">
              {allServiceTickets.length} service ticket
              {allServiceTickets.length !== 1 ? "s" : ""} scheduled
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : allServiceTickets.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No service tickets scheduled"
        />
      ) : (
        <div className="bg-white rounded-lg border">
          <Table
            dataSource={allServiceTickets}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} service tickets`,
            }}
            scroll={{ x: 1200 }}
          />
        </div>
      )}

      <Modal
        title="Service Details"
        open={!!selectedService}
        onCancel={() => setSelectedService(null)}
        footer={null}
        width={800}
      >
        {selectedService && (
          <div
            className="cursor-pointer"
            onClick={() => {
              const bookingId = selectedService.booking?.id;
              if (bookingId) {
                window.location.href = `/bookings/${bookingId}?tab=services&serviceId=${selectedService.id}`;
              }
            }}
          >
            <ContainerInfoCard
              serviceTicket={selectedService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
